import React, { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { InView } from "react-intersection-observer";
import { useQuery } from "@apollo/client";
import { useLocation } from "react-router-dom";
import { ContentQuery } from "arena/queries";
import ShortyPlayer from "./ShortyPlayer";
import ShortyThumbnail from "./ShortyThumbnail";
import { CloseRouteDialogButton } from "arena/components/RouteDialog";
import contentUrl from "core/utils/contentUrl";

export default function ShortyVirtualItem({
  content,
  productId,
  collectionId,
  index,
  isInitial = false,
  isActive = false,
  shouldPreload = false,
  onActivate,
  onNavigate,
  totalItems,
}) {
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down("md"));
  const [hasBeenActive, setHasBeenActive] = useState(isInitial || isActive);
  const location = useLocation();

  // Обновляем hasBeenActive когда элемент становится активным
  React.useEffect(() => {
    if (isActive) {
      setHasBeenActive(true);
    }
  }, [isActive]);

  // Загружаем полные данные контента когда элемент становится активным
  const { data: fullContentData } = useQuery(ContentQuery, {
    variables: { contentId: content?.id },
    skip: !isActive || !content?.id, // Запрашиваем только когда элемент активен
    fetchPolicy: "cache-and-network",
  });

  // Используем полный контент если он загружен, иначе базовый
  const contentToUse = fullContentData?.content || content;

  // Функции навигации
  const handlePrevious = () => {
    if (index > 0 && onNavigate) {
      onNavigate(index - 1);
    }
  };

  const handleNext = () => {
    if (index < totalItems - 1 && onNavigate) {
      onNavigate(index + 1);
    }
  };

  const canGoPrevious = index > 0;
  const canGoNext = index < totalItems - 1;

  const handleInViewChange = (inView) => {
    // Активируем когда элемент входит в зону видимости
    if (inView && !isActive && content && onActivate) {
      onActivate(); // Говорим родителю что этот элемент должен стать активным
      setHasBeenActive(true);

      // Обновляем URL когда элемент становится активным
      const expectedUrl = contentUrl(content.siteUrl, {
        productId,
        collectionId,
      });

      // Проверяем нужно ли обновить URL
      const currentPath = location.pathname + location.search;
      if (currentPath !== expectedUrl) {
        // Используем history API напрямую чтобы избежать ререндера
        window.history.replaceState({}, "", expectedUrl);
      }

      // Загрузка контента теперь централизована в ShortyVirtualFeed
      // через handleNavigate и handleActivateDebounced
    }
    // НЕ деактивируем автоматически - пусть остается активным пока другой не станет активным
    // Это предотвратит "моргание" между элементами
  };

  if (!content) {
    return (
      <Box
        sx={{
          width: "100%",
          height: isMobileView
            ? "100dvh" // Dynamic viewport height для мобильных
            : "calc(100vh - 64px)", // На десктопе обычный vh минус header
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          scrollSnapAlign: "start",
          scrollSnapStop: "always",
          flexShrink: 0, // Предотвращаем сжатие элемента
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            maxWidth: "400px",
            height: 0,
            paddingTop: "177.78%", // 9:16
            // position: "relative",
            backgroundColor: "#111",
            // borderRadius: 2,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "#666",
              fontSize: "14px",
            }}
          >
            Loading...
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <InView
      threshold={0.75}
      rootMargin="-5% 0px -5% 0px"
      onChange={handleInViewChange}
      triggerOnce={false}
    >
      <Box
        sx={{
          width: "100%",
          height: isMobileView
            ? "100dvh" // Dynamic viewport height для мобильных
            : "calc(100vh - 64px)", // На десктопе обычный vh минус header
          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          scrollSnapAlign: "start",
          scrollSnapStop: "always",
          flexShrink: 0, // Предотвращаем сжатие элемента
          position: "relative",
          // Убираем margin для точной навигации в полноэкранном режиме
        }}
      >
        {isActive || shouldPreload ? (
          // Всегда рендерим плеер для активного или предзагружаемого элемента
          <ShortyPlayer
            content={contentToUse}
            contentId={contentToUse.id}
            productId={productId}
            collectionId={collectionId}
            closeRouteDialogButton={
              isActive ? <CloseRouteDialogButton /> : null
            }
            preload={shouldPreload && !isActive} // Предзагрузка для неактивного элемента
            shouldStart={isActive} // Старт только когда активен
            autoPlay={isActive} // Автоплей только для активных
            muted={false} // Приглушение для предзагрузки
            showNextPreview={isActive} // Превью только для активных
            onPrevious={isActive ? handlePrevious : () => {}}
            onNext={isActive ? handleNext : () => {}}
            canGoPrevious={isActive ? canGoPrevious : false}
            canGoNext={isActive ? canGoNext : false}
          />
        ) : (
          // Показываем thumbnail для остальных элементов
          <ShortyThumbnail
            content={content}
            productId={productId}
            collectionId={collectionId}
            hasBeenActive={hasBeenActive}
          />
        )}
      </Box>
    </InView>
  );
}

ShortyVirtualItem.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    siteUrl: PropTypes.string.isRequired,
    verticalThumbnails: PropTypes.array,
    thumbnails: PropTypes.array,
  }),
  productId: PropTypes.string,
  collectionId: PropTypes.string,
  index: PropTypes.number.isRequired,
  isInitial: PropTypes.bool,
  isActive: PropTypes.bool,
  shouldPreload: PropTypes.bool,
  onActivate: PropTypes.func,
  onLoadMore: PropTypes.func,
  onNavigate: PropTypes.func,
  totalItems: PropTypes.number,
};
